import * as Sentry from "@sentry/react";
import { Component, ReactNode } from "react";
import { AlertTriangle, RefreshCw, Home, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface ErrorFallbackProps {
  error: Error;
  resetError: () => void;
  componentStack?: string;
  eventId?: string;
}

// Graceful fallback UI component
function ErrorFallback({ error, resetError, componentStack, eventId }: ErrorFallbackProps) {
  const isProduction = import.meta.env.PROD;

  const handleGoHome = () => {
    window.location.href = '/';
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="max-w-lg w-full">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
            <AlertTriangle className="w-8 h-8 text-destructive" />
          </div>
          <CardTitle className="text-xl">Something went wrong</CardTitle>
          <CardDescription>
            We apologize for the inconvenience. Our team has been notified and is working to fix the issue.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {!isProduction && (
            <Collapsible>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" className="w-full justify-between">
                  <span>View Error Details</span>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <div className="rounded-md bg-muted p-4 space-y-3">
                  <div>
                    <p className="text-sm font-medium text-destructive">{error.name}</p>
                    <p className="text-sm text-muted-foreground break-words">{error.message}</p>
                  </div>
                  {componentStack && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Component Stack:</p>
                      <pre className="text-xs overflow-auto max-h-40 bg-background p-2 rounded">
                        {componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}

          {eventId && (
            <p className="text-xs text-center text-muted-foreground">
              Error ID: <code className="bg-muted px-1 py-0.5 rounded">{eventId}</code>
            </p>
          )}
        </CardContent>

        <CardFooter className="flex gap-3 justify-center">
          <Button variant="outline" onClick={handleGoHome}>
            <Home className="w-4 h-4 mr-2" />
            Go Home
          </Button>
          <Button onClick={resetError || handleRefresh}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

// Page-level error fallback with minimal UI
function PageErrorFallback({ error, resetError }: ErrorFallbackProps) {
  return (
    <div className="w-full p-8 flex flex-col items-center justify-center text-center">
      <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" />
      <h3 className="text-lg font-medium mb-2">This section couldn't load</h3>
      <p className="text-sm text-muted-foreground mb-4 max-w-md">
        There was a problem loading this content. Please try again.
      </p>
      <Button variant="outline" size="sm" onClick={resetError}>
        <RefreshCw className="w-4 h-4 mr-2" />
        Retry
      </Button>
    </div>
  );
}

// Component-level error fallback (inline, minimal)
function ComponentErrorFallback({ resetError }: ErrorFallbackProps) {
  return (
    <div className="p-4 border border-destructive/20 rounded-lg bg-destructive/5 text-center">
      <p className="text-sm text-muted-foreground mb-2">Failed to load component</p>
      <Button variant="ghost" size="sm" onClick={resetError}>
        <RefreshCw className="w-3 h-3 mr-1" />
        Retry
      </Button>
    </div>
  );
}

// HOC to wrap components with error boundary
interface WithErrorBoundaryOptions {
  fallback?: 'full' | 'page' | 'component';
  onError?: (error: Error, componentStack: string) => void;
}

export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  options: WithErrorBoundaryOptions = {}
) {
  const { fallback = 'component', onError } = options;

  return function WithErrorBoundaryWrapper(props: P) {
    const FallbackComponent =
      fallback === 'full' ? ErrorFallback :
      fallback === 'page' ? PageErrorFallback :
      ComponentErrorFallback;

    return (
      <Sentry.ErrorBoundary
        fallback={(errorData) => (
          <FallbackComponent
            error={errorData.error instanceof Error ? errorData.error : new Error(String(errorData.error))}
            resetError={errorData.resetError}
            componentStack={errorData.componentStack}
            eventId={errorData.eventId}
          />
        )}
        onError={(error, componentStack) => {
          onError?.(error instanceof Error ? error : new Error(String(error)), componentStack);
        }}
        showDialog={false}
      >
        <WrappedComponent {...props} />
      </Sentry.ErrorBoundary>
    );
  };
}

// Main App Error Boundary Component
interface AppErrorBoundaryProps {
  children: ReactNode;
}

export function AppErrorBoundary({ children }: AppErrorBoundaryProps) {
  return (
    <Sentry.ErrorBoundary
      fallback={(errorData) => (
        <ErrorFallback
          error={errorData.error instanceof Error ? errorData.error : new Error(String(errorData.error))}
          resetError={errorData.resetError}
          componentStack={errorData.componentStack}
          eventId={errorData.eventId}
        />
      )}
      showDialog={false}
      beforeCapture={(scope) => {
        scope.setTag('boundary', 'app');
        scope.setLevel('fatal');
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

// Page Error Boundary for route-level errors
export function PageErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <Sentry.ErrorBoundary
      fallback={(errorData) => (
        <PageErrorFallback
          error={errorData.error instanceof Error ? errorData.error : new Error(String(errorData.error))}
          resetError={errorData.resetError}
          componentStack={errorData.componentStack}
          eventId={errorData.eventId}
        />
      )}
      showDialog={false}
      beforeCapture={(scope) => {
        scope.setTag('boundary', 'page');
        scope.setLevel('error');
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

// Section Error Boundary for component sections
export function SectionErrorBoundary({ children, name }: { children: ReactNode; name?: string }) {
  return (
    <Sentry.ErrorBoundary
      fallback={(errorData) => (
        <ComponentErrorFallback
          error={errorData.error instanceof Error ? errorData.error : new Error(String(errorData.error))}
          resetError={errorData.resetError}
        />
      )}
      showDialog={false}
      beforeCapture={(scope) => {
        scope.setTag('boundary', 'section');
        if (name) {
          scope.setTag('section.name', name);
        }
        scope.setLevel('warning');
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

export { ErrorFallback, PageErrorFallback, ComponentErrorFallback };
